import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Die kurze Meldung unten, mit einem Weg zurück.
 *
 * Der Grund, warum es sie gibt: eine Rückfrage vor jedem Löschen kostet einen
 * Klick *und* macht das Löschen endgültig — beides falsch herum. Besser ist,
 * es geschieht sofort und lässt sich zurücknehmen. Dafür braucht es eine
 * Stelle, an der „rückgängig" steht, ohne dass sie den Blick festhält.
 *
 * Sie liegt über der Seite und verschiebt deshalb nichts.
 */

type Hinweis = {
  id: number;
  text: ReactNode;
  /** Beschriftung und Wirkung des Knopfes rechts. */
  aktion?: { label: string; onClick: () => void };
};

type Ctx = {
  zeigen: (text: ReactNode, aktion?: Hinweis["aktion"], dauer?: number) => void;
};

const HinweisCtx = createContext<Ctx>({ zeigen: () => {} });

export const useHinweis = () => useContext(HinweisCtx);

export function HinweisLayer({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Hinweis[]>([]);
  const naechste = useRef(1);

  const schliessen = useCallback((id: number) => {
    setListe((v) => v.filter((h) => h.id !== id));
  }, []);

  const zeigen = useCallback<Ctx["zeigen"]>((text, aktion, dauer = 6000) => {
    const id = naechste.current++;
    setListe((v) => [...v, { id, text, aktion }]);
    // Sechs Sekunden: lang genug, um „rückgängig" zu lesen und zu treffen,
    // kurz genug, dass die Meldung nicht Teil der Seite wird.
    window.setTimeout(() => schliessen(id), dauer);
  }, [schliessen]);

  const wert = useMemo(() => ({ zeigen }), [zeigen]);

  return (
    <HinweisCtx.Provider value={wert}>
      {children}
      {liste.length > 0 && createPortal(
        <div className="ui-hinweise" role="status" aria-live="polite">
          {liste.map((h) => (
            <div key={h.id} className="ui-hinweis">
              <span className="ui-hinweis-text">{h.text}</span>
              {h.aktion && (
                <button
                  type="button"
                  className="ui-hinweis-aktion"
                  onClick={() => { schliessen(h.id); h.aktion!.onClick(); }}
                >
                  {h.aktion.label}
                </button>
              )}
              <button
                type="button"
                className="ui-hinweis-zu"
                aria-label="Dismiss"
                onClick={() => schliessen(h.id)}
              >
                <X />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </HinweisCtx.Provider>
  );
}
